import { css } from '@emotion/css';
import { UsageTrackerEvent, usageTracker } from '@volumental/analytic';
import { Content, Headline, StarRating } from '@volumental/common';
import { type JSX, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { type SizeLocale } from '../../../api/gql/graphql';
import { Modal } from '../../../components/modal/modal';
import { useScannerStore, useSessionStore, useViewStore } from '../../../store';
import { FeedbackButtons } from './feedback-buttons';
import { FeedbackCommentBox } from './feedback-comment-box';

const titleStyle = css`
  font-size: 1rem;
  font-weight: normal;
  line-height: normal;
  margin-top: unset;
`;
const ratingStyle = css`
  padding-top: 25px;
  padding-bottom: 20px;
  display: flex;
  justify-content: center;
`;

interface FeedbackProps {
  show: boolean;
  onClose: (feedback: boolean) => void;
  closeModal: () => void;
  pdpDetail?: { local: SizeLocale; gender?: string; recommendedSize: string };
  scanID?: string;
  previousPurchaseID?: string;
  trackingEvent?: UsageTrackerEvent;
  title?: string;
}

export function Feedback({
  show,
  onClose,
  closeModal,
  pdpDetail,
  scanID,
  previousPurchaseID,
  trackingEvent = UsageTrackerEvent.Feedback,
  title,
}: FeedbackProps): JSX.Element {
  const { mode, productID } = useSessionStore((store) => store);
  const { currentView } = useViewStore((store) => store);
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const { flowType } = useScannerStore((store) => store);

  const ratingNPSTitles: string[] = t('feedbackModal.npsSubtitle', {
    returnObjects: true,
  }) as Array<string>;

  const uploadFeeback = (): void => {
    // uploadFeedback
    const feedback: Record<string, string | undefined> = {
      Rating: rating.toString(),
      Comment: comment,
      recommended_size: pdpDetail?.recommendedSize,
      productID: productID ?? 'UNKNOWN',
      size_local: pdpDetail?.local,
      product_gender: pdpDetail?.gender,
      sizing_flow_type: flowType ?? 'UNKNOWN',
      previous_purchase_id: previousPurchaseID ?? 'NO ID',
      scanID: scanID ?? 'NO ID',
      experience: mode,
      current_page: currentView ?? 'UNKNOWN',
    };
    usageTracker.track(trackingEvent, feedback);
    onClose(true);
  };
  const noAnswer = (): void => {
    onClose(false);
  };

  return (
    <Modal
      show={show}
      close={closeModal}
      modalTitle={t('feedbackModal.ratingTitle')}
    >
      <Content disableGutters disableScrollbar>
        <Headline className={titleStyle}>
          <Trans>{title ?? t('feedbackModal.ratingTitle')}</Trans>
        </Headline>
        <div className={ratingStyle}>
          <StarRating
            onRatingSelect={(userRating) => {
              setRating(userRating);
            }}
            npsTitles={ratingNPSTitles}
            altImage={t('imageAlt.starIcon')}
          />
        </div>
        <FeedbackCommentBox
          setComment={(userComment) => {
            setComment(userComment);
          }}
        />
      </Content>
      <FeedbackButtons
        onSubmit={uploadFeeback}
        onSkip={noAnswer}
        isSubmitButtonDisabled={comment === '' && rating === 0}
      />
    </Modal>
  );
}
